import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { ref, getBlob } from "firebase/storage";
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  DocumentIcon,
} from "@heroicons/react/20/solid";
import { storage } from "../../firebase";

const ACCENT = "#1b4d2e";

function isImageMime(mimeType, filename) {
  if (mimeType?.startsWith("image/")) return true;
  if (filename && /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(filename)) return true;
  return false;
}

function isPdfMime(mimeType, filename) {
  if (mimeType === "application/pdf") return true;
  if (filename && /\.pdf$/i.test(filename)) return true;
  return false;
}

export default function ArchivoPreviewModal({
  open,
  onClose,
  storagePath,
  filename,
  mimeType,
}) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !storagePath) return;
    let cancelled = false;
    let url = null;
    setLoading(true);
    setError(null);
    setObjectUrl(null);
    getBlob(ref(storage, storagePath))
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch((err) => {
        console.error("ArchivoPreviewModal load error:", err);
        if (!cancelled) setError("No se pudo cargar el archivo.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, storagePath]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const handleDownload = () => {
    if (!objectUrl) return;
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "archivo";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const showAsImage = isImageMime(mimeType, filename);
  const showAsPdf = !showAsImage && isPdfMime(mimeType, filename);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative flex h-[90vh] w-[min(95vw,1100px)] flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-zinc-200 px-4 py-2.5">
          <DocumentIcon className="h-5 w-5 shrink-0 text-zinc-400" />
          <div className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-800">
            {filename || "Archivo"}
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!objectUrl}
            className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-40"
            title="Descargar"
            aria-label="Descargar"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
            title="Cerrar"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-zinc-100">
          {loading && (
            <div className="flex h-full items-center justify-center text-sm text-zinc-500">
              Cargando...
            </div>
          )}
          {error && (
            <div className="flex h-full items-center justify-center text-sm text-red-600">
              {error}
            </div>
          )}
          {!loading && !error && objectUrl && (
            showAsImage ? (
              <div className="flex h-full items-center justify-center p-4">
                <img
                  src={objectUrl}
                  alt={filename || "Archivo"}
                  className="max-h-full max-w-full object-contain shadow"
                />
              </div>
            ) : showAsPdf ? (
              <iframe
                src={objectUrl}
                title={filename || "Archivo"}
                className="h-full w-full border-0 bg-white"
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                <div className="text-sm text-zinc-600">
                  Este tipo de archivo no se puede previsualizar en el navegador.
                </div>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                  style={{ backgroundColor: ACCENT }}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Descargar
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
